import { init } from "next/dist/compiled/webpack/webpack";
import { fromPromise, setup } from "xstate";

import { getDocumentDirPath } from "../../../data/filesystem";
import { LOCAL_WORKSPACE_NAME } from "../../../strings";
import { docsApi } from "../../api/docsapi";
import { filesystemApi } from "../../api/filesystem";
import { sqliteApi } from "../../api/sqlite";
import { type Workspace } from "../../types/workspace.types";
import { authenticationActorRef } from "../authentication/index";
import { setRepoPublished } from "../docsapi/setRepoPublished";

// const debuglog = (_msg: string, ...args: unknown[]) =>
// 	console.log(`[delete document machine] ${_msg}`, ...args);

const debuglog = (_msg: string, ..._args: unknown[]) => {};

const deleteDocumentFromDisk = fromPromise<void, { documentId: string }>(async ({ input }) => {
	const path = await getDocumentDirPath(input.documentId);

	await filesystemApi.document.delete(path);
});

const deleteDocumentFromRemote = fromPromise<
	void,
	{ documentId: string; workspaceNames: string[] }
>(async ({ input }) => {
	debuglog("Deleting document from workspaces", input.documentId, input.workspaceNames);
	await Promise.all(
		input.workspaceNames.map((workspaceName) => {
			if (workspaceName === LOCAL_WORKSPACE_NAME) {
				return;
			}
			debuglog("deleting...", workspaceName, input.documentId);
			return docsApi.document.deleteRemote(workspaceName, input.documentId);
		})
	);
});

const deleteDocumentFromCache = fromPromise<void, { documentId: string }>(async ({ input }) => {
	await Promise.all([
		sqliteApi.documents.delete(input.documentId),
		sqliteApi.documentWorkspaces.deleteByDocumentId(input.documentId),
	]);
});

type DeleteDocumentContext = {
	documentId: string;
	workspaces: Workspace[];
};
export const deleteDocumentMachine = setup({
	types: {
		context: {} as DeleteDocumentContext,
		input: {} as {
			documentId: string;
			workspaces: Workspace[];
		},
	},
	actors: {
		deleteDocumentFromDisk,
		deleteDocumentFromRemote,
		deleteDocumentFromCache,
	},
}).createMachine({
	id: "delete-document",
	type: "parallel",
	context: ({ input }) => {
		return {
			documentId: input.documentId,
			workspaces: input.workspaces,
		} satisfies DeleteDocumentContext;
	},
	states: {
		deleteRemoteDocument: {
			initial: "delete",
			states: {
				delete: {
					invoke: {
						src: "deleteDocumentFromRemote",
						input: ({ context }) => ({
							documentId: context.documentId,
							workspaceNames: context.workspaces
								.filter((w) => w.name !== LOCAL_WORKSPACE_NAME)
								.map((w) => w.slug),
						}),
						onError: {
							actions: [
								({ event }) => {
									console.error("Error deleting document from remote", {
										cause: event.error,
										event,
									});
								},
							],
							target: "error",
						},
						onDone: {
							target: "done",
						},
					},
				},
				done: {
					type: "final",
				},
				error: {
					after: {
						3000: {
							target: "delete",
						},
					},
				},
			},
		},
		deleteFromDisk: {
			initial: "delete",
			states: {
				delete: {
					invoke: {
						src: "deleteDocumentFromDisk",
						input: ({ context }: { context: DeleteDocumentContext }) => ({
							documentId: context.documentId,
						}),
						onError: {
							actions: [
								({ event }) => {
									console.error("Error deleting document from disk", {
										cause: event.error,
										event,
									});
								},
								({ event }) => {
									throw event.error;
								},
							],
							target: "error",
						},
						onDone: {
							target: "done",
						},
					},
				},
				done: {
					type: "final",
				},
				error: {
					type: "final",
					output: ({ context, event }) => {
						return {
							documentId: context.documentId,
							error: event.error,
						};
					},
				},
			},
		},

		deletingFromCache: {
			initial: "delete",
			states: {
				delete: {
					invoke: {
						src: "deleteDocumentFromCache",
						input: ({ context }) => ({
							documentId: context.documentId,
						}),
						onError: {
							actions: [
								({ event }) => {
									console.error("Error deleting document from cache", {
										cause: event.error,
										event,
									});
								},
								({ event }) => {
									throw event.error;
								},
							],
							target: "error",
						},
						onDone: {
							target: "done",
						},
					},
				},
				done: {
					type: "final",
				},
				error: {
					type: "final",
				},
			},
		},
	},
});
